// ════════════════════════════════════════════════════════════
//  JoinBar.jsx — under a friend's Mark: where they are + ONE button
// ════════════════════════════════════════════════════════════
//  caP'n 2026-10-18: "click on each other's Mark and easily join whatever room
//  they're in". The Mark itself is the Empire's kit (vendored byte-identical,
//  never edited here), so the join lives in this title-local bar that sits
//  under it. Friends get the Join button (your partner is one automatically —
//  partnerBond.js). ANYONE ELSE — a stranger in your box, someone's partner — gets
//  the friend button: Add friend · Accept (they asked you) · Request sent
//  (caP'n 2026-10-20: "click on any player's mark and add them as a friend").
//  A blocked player shows nothing. It looks again every few seconds while the
//  Mark is open, never more.
// ════════════════════════════════════════════════════════════
import { useEffect, useState } from "react";
import { fbNet } from "../online/net.js";
import { whereabouts } from "../online/together.js";
import { friendButton, sendRequest, acceptRequest } from "../online/friendship.js";
import { say } from "../data/text.js";

const LOOK_MS = 4000;   // ⟵ DIAL how often an open Mark re-checks where they are

const LABEL = {
  roomfight: (w) => say("join.where.roomfight", { code: w.room }),
  room: (w) => say("join.where.room", { code: w.room }),
  fight: () => say("join.where.fight"),
  wait: (w) => say(w.pub ? "join.where.waitpub" : "join.where.wait"),
  watch: () => say("join.where.watch"),
  none: () => say("join.where.none"),
};
const BUTTON = { roomfight: "join.watchfight", fight: "join.watchfight", room: "join.room", wait: "join.them", watch: "join.sit" };

const ASK = { add: "friend.add", accept: "friend.accept", sent: "friend.sent" };

export default function JoinBar({ uid, me, myName, onJoin, toast }) {
  const [friend, setFriend] = useState(null);   // null = not known yet · false = not a friend
  const [w, setW] = useState(null);
  const [ask, setAsk] = useState(null);         // not friends: "add" | "accept" | "sent" | null (blocked)
  const [busy, setBusy] = useState(false);
  const [poke, setPoke] = useState(0);          // re-look right after a friend button
  useEffect(() => {
    if (!uid || !me || uid === me) return undefined;
    let dead = false, t = null;
    if (!poke) { setFriend(null); setW(null); setAsk(null); }   // another Mark: never show the last one's whereabouts
    const look = async () => {
      const edge = await fbNet.get(`profiles/${me}/friends/${uid}`).catch(() => null);
      const ok = !!(edge && edge.status === "accepted");
      const where = ok ? await whereabouts(uid) : null;
      const name = ok || (edge && edge.name) ? (edge && edge.name) || null : await fbNet.get(`profiles/${uid}/name`).catch(() => null);
      if (dead) return;
      setFriend(ok ? { name } : { name, stranger: true }); setW(where); setAsk(ok ? null : friendButton(edge));
      t = setTimeout(look, LOOK_MS);
    };
    look();
    return () => { dead = true; clearTimeout(t); };
  }, [uid, me, poke]);
  if (!friend) return null;
  if (friend.stranger) {
    if (!ask) return null;   // blocked (either way): nothing to offer
    const go = async () => {
      if (busy || ask === "sent") return;
      setBusy(true);
      const r = ask === "accept" ? await acceptRequest(me, myName, uid, friend.name) : await sendRequest(me, myName, uid, friend.name);
      setBusy(false);
      if (!r.ok) { toast && toast(r.error); return; }
      toast && toast(say(ask === "accept" ? "friend.accepted" : "friend.requested", { name: friend.name || "" }));
      setPoke((n) => n + 1);
    };
    return (
      <div className="join-bar" role="group" aria-label="Friends">
        <span className="jb-where none">{say(ask === "accept" ? "friend.theyasked" : "friend.notyet", { name: friend.name || "" })}</span>
        <button className={"btn" + (ask === "sent" ? "" : " primary")} disabled={busy || ask === "sent"} onClick={go}>{say(ASK[ask])}</button>
      </div>
    );
  }
  if (!w) return null;
  const btn = BUTTON[w.kind];
  return (
    <div className="join-bar" role="group" aria-label="Join">
      <span className={"jb-where" + (btn ? "" : " none")}>{LABEL[w.kind](w)}</span>
      {btn && <button className="btn primary" onClick={() => onJoin(uid, w.name || friend.name)}>{say(btn)}</button>}
    </div>
  );
}
